import { useState, useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { VoicePipelineLatencyDistributionBucket } from '@super/shared-types'
import { getPipelineChartColors } from '@/lib/constants/chart-colors'
import { useTheme } from '@/contexts/theme.context'

interface Props {
  data: VoicePipelineLatencyDistributionBucket[] | undefined
  isLoading: boolean
  bucketWidthMs?: number
  bucketCount?: number
  rangeStartMs?: number
}

function CustomTooltip({
  active,
  payload,
  label,
  bucketWidth,
}: {
  active?: boolean
  payload?: { payload: VoicePipelineLatencyDistributionBucket }[]
  label?: number
  bucketWidth: number
}) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-white dark:bg-background border border-gray-200 dark:border-border rounded shadow-sm p-2 text-xs space-y-0.5">
      <p className="font-medium text-gray-700 dark:text-foreground">
        {Math.round(label ?? 0)}–{Math.round((label ?? 0) + bucketWidth)} ms
      </p>
      <p className="text-gray-500 dark:text-muted-foreground">
        {d.percentage}% of turns ({d.count} turns)
      </p>
    </div>
  )
}

export function LatencyDistributionChart({
  data,
  isLoading,
  bucketWidthMs,
  bucketCount,
  rangeStartMs,
}: Props) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const colors = getPipelineChartColors(isDark)
  const gridColor = isDark ? '#2a2a2a' : '#f0f0f0'
  const cursorColor = isDark ? '#374151' : '#e5e7eb'
  const bucketWidth = bucketWidthMs ?? 200
  const numBuckets = bucketCount ?? 20
  const startMs = rangeStartMs ?? 0
  const [selectedModel, setSelectedModel] = useState<string>('')

  const models = useMemo(() => {
    if (!data) return []
    return Array.from(new Set(data.map(b => b.model))).sort()
  }, [data])

  const effectiveModel =
    selectedModel && models.includes(selectedModel)
      ? selectedModel
      : (models[0] ?? '')

  const rows = useMemo(() => {
    if (!data || !effectiveModel) return []
    const byBucket = new Map(
      data.filter(b => b.model === effectiveModel).map(b => [b.bucketMs, b]),
    )
    return Array.from({ length: numBuckets }, (_, i) => {
      const ms = startMs + i * bucketWidth
      return (
        byBucket.get(ms) ?? {
          model: effectiveModel,
          bucketMs: ms,
          count: 0,
          percentage: 0,
        }
      )
    })
  }, [data, effectiveModel, bucketWidth, numBuckets, startMs])

  return (
    <Card className="border border-gray-200 dark:border-border dark:bg-background">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CardTitle className="tracking-tight text-gray-900 dark:text-foreground font-space-grotesk text-md sm:text-lg font-bold mb-1">
            Latency Frequency Distribution
          </CardTitle>
          {models.length > 0 && (
            <Select value={effectiveModel} onValueChange={setSelectedModel}>
              <SelectTrigger className="w-48 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {models.map(m => (
                  <SelectItem key={m} value={m} className="text-xs">
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <p className="text-xs text-gray-400 dark:text-muted-foreground mt-1">
          Percentage of turns by total latency (P99 range)
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading || !data ? (
          <Skeleton className="h-64 w-full" />
        ) : rows.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-xs text-gray-400 dark:text-muted-foreground">
            No data for this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={rows}
              margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              barCategoryGap={0}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={gridColor}
                vertical={false}
              />
              <XAxis
                dataKey="bucketMs"
                tickFormatter={v => `${Math.round(Number(v))}`}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                label={{
                  value: 'Total Latency (ms)',
                  position: 'insideBottom',
                  offset: -2,
                  fontSize: 10,
                  fill: '#9ca3af',
                }}
                height={32}
              />
              <YAxis
                tickFormatter={v => `${v}%`}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                width={36}
                allowDecimals={false}
              />
              <Tooltip
                content={<CustomTooltip bucketWidth={bucketWidth} />}
                cursor={{ fill: cursorColor, strokeWidth: 0 }}
              />
              <Bar
                dataKey="percentage"
                fill={colors.distribution}
                fillOpacity={0.3}
                stroke={colors.distribution}
                strokeWidth={1}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
